import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h3>{`Description`}</h3>
    <p>{`This document is all you need to know about what's required in your
package.json file.  It must be actual JSON, not just a JavaScript object
literal.`}</p>
    <p>{`A lot of the behavior described in this document is affected by the config
settings described in `}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/config"
      }}><inlineCode parentName="a">{`config`}</inlineCode></a>{`.`}</p>
    <h3>{`name`}</h3>
    <p>{`If you plan to publish your package, the `}<em parentName="p">{`most`}</em>{` important things in your
package.json are the name and version fields as they will be required. The
name and version together form an identifier that is assumed to be
completely unique.  Changes to the package should come along with changes
to the version. If you don't plan to publish your package, the name and
version fields are optional.`}</p>
    <p>{`The name is what your thing is called.`}</p>
    <p>{`Some rules:`}</p>
    <ul>
      <li parentName="ul">{`The name must be less than or equal to 214 characters. This includes the
scope for scoped packages.`}</li>
      <li parentName="ul">{`The names of scoped packages can begin with a dot or an underscore. This
is not permitted without a scope.`}</li>
      <li parentName="ul">{`New packages must not have uppercase letters in the name.`}</li>
      <li parentName="ul">{`The name ends up being part of a URL, an argument on the command line,
and a folder name. Therefore, the name can't contain any non-URL-safe
characters.`}</li>
    </ul>
    <p>{`Some tips:`}</p>
    <ul>
      <li parentName="ul">{`Don't use the same name as a core Node module.`}</li>
      <li parentName="ul">{`Don't put "js" or "node" in the name.  It's assumed that it's js, since
you're writing a package.json file, and you can specify the engine using
the "engines" field.  (See below.)`}</li>
      <li parentName="ul">{`The name will probably be passed as an argument to require(), so it
should be something short, but also reasonably descriptive.`}</li>
      <li parentName="ul">{`You may want to check the npm registry to see if there's something by
that name already, before you get too attached to it.
`}<a parentName="li" {...{
          "href": "https://www.npmjs.com/"
        }}>{`https://www.npmjs.com/`}</a></li>
    </ul>
    <p>{`A name can be optionally prefixed by a scope, e.g. `}<inlineCode parentName="p">{`@myorg/mypackage`}</inlineCode>{`. See
`}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/scope"
      }}><inlineCode parentName="a">{`scope`}</inlineCode></a>{` for more detail.`}</p>
    <h3>{`version`}</h3>
    <p>{`If you plan to publish your package, the `}<em parentName="p">{`most`}</em>{` important things in your
package.json are the name and version fields as they will be required. The
name and version together form an identifier that is assumed to be
completely unique.  Changes to the package should come along with changes
to the version. If you don't plan to publish your package, the name and
version fields are optional.`}</p>
    <p>{`Version must be parseable by
`}<a parentName="p" {...{
        "href": "https://github.com/npm/node-semver"
      }}>{`node-semver`}</a>{`, which is bundled with
npm as a dependency.  (`}<inlineCode parentName="p">{`npm install semver`}</inlineCode>{` to use it yourself.)`}</p>
    <h3>{`description`}</h3>
    <p>{`Put a description in it.  It's a string.  This helps people discover your
package, as it's listed in `}<inlineCode parentName="p">{`npm search`}</inlineCode>{`.`}</p>
    <h3>{`keywords`}</h3>
    <p>{`Put keywords in it.  It's an array of strings.  This helps people discover
your package as it's listed in `}<inlineCode parentName="p">{`npm search`}</inlineCode>{`.`}</p>
    <h3>{`homepage`}</h3>
    <p>{`The url to the project homepage.`}</p>
    <p>{`Example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"homepage": "https://github.com/owner/project#readme"
`}</code></pre>
    <h3>{`bugs`}</h3>
    <p>{`The url to your project's issue tracker and / or the email address to which
issues should be reported. These are helpful for people who encounter
issues with your package.`}</p>
    <p>{`It should look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "url" : "https://github.com/owner/project/issues",
  "email" : "project@hostname.com"
}
`}</code></pre>
    <p>{`You can specify either one or both values. If you want to provide only a
url, you can specify the value for "bugs" as a simple string instead of an
object.`}</p>
    <p>{`If a url is provided, it will be used by the `}<inlineCode parentName="p">{`npm bugs`}</inlineCode>{` command.`}</p>
    <h3>{`license`}</h3>
    <p>{`You should specify a license for your package so that people know how they
are permitted to use it, and any restrictions you're placing on it.`}</p>
    <p>{`If you're using a common license such as BSD-2-Clause or MIT, add a current
SPDX license identifier for the license you're using, like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "license" : "BSD-3-Clause"
}
`}</code></pre>
    <p>{`You can check `}<a parentName="p" {...{
        "href": "https://spdx.org/licenses/"
      }}>{`the full list of SPDX license
IDs`}</a>{`.  Ideally you should pick one that is
`}<a parentName="p" {...{
        "href": "https://opensource.org/licenses/alphabetical"
      }}>{`OSI`}</a>{` approved.`}</p>
    <p>{`If your package is licensed under multiple common licenses, use an `}<a parentName="p" {...{
        "href": "https://spdx.dev/specifications/"
      }}>{`SPDX
license expression syntax version 2.0
string`}</a>{`, like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "license" : "(ISC OR GPL-3.0)"
}
`}</code></pre>
    <p>{`If you are using a license that hasn't been assigned an SPDX identifier, or if
you are using a custom license, use a string value like this one:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "license" : "SEE LICENSE IN <filename>"
}
`}</code></pre>
    <p>{`Then include a file named `}<inlineCode parentName="p">{`<filename>`}</inlineCode>{` at the top level of the package.`}</p>
    <p>{`Some old packages used license objects or a "licenses" property containing
an array of license objects:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`// Not valid metadata
{
  "license" : {
    "type" : "ISC",
    "url" : "https://opensource.org/licenses/ISC"
  }
}

// Not valid metadata
{
  "licenses" : [
    {
      "type": "MIT",
      "url": "https://www.opensource.org/licenses/mit-license.php"
    },
    {
      "type": "Apache-2.0",
      "url": "https://opensource.org/licenses/apache2.0.php"
    }
  ]
}
`}</code></pre>
    <p>{`Those styles are now deprecated. Instead, use SPDX expressions, like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "license": "ISC"
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "license": "(MIT OR Apache-2.0)"
}
`}</code></pre>
    <p>{`Finally, if you do not wish to grant others the right to use a private or
unpublished package under any terms:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "license": "UNLICENSED"
}
`}</code></pre>
    <p>{`Consider also setting `}<inlineCode parentName="p">{`"private": true`}</inlineCode>{` to prevent accidental publication.`}</p>
    <h3>{`people fields: author, contributors`}</h3>
    <p>{`The "author" is one person.  "contributors" is an array of people.  A
"person" is an object with a "name" field and optionally "url" and "email",
like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name" : "Barney Rubble",
  "email" : "b@rubble.com",
  "url" : "http://barnyrubble.tumblr.com/"
}
`}</code></pre>
    <p>{`Or you can shorten that all into a single string, and npm will parse it for
you:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "author": "Barney Rubble <b@rubble.com> (http://barnyrubble.tumblr.com/)"
}
`}</code></pre>
    <p>{`Both email and url are optional either way.`}</p>
    <p>{`npm also sets a top-level "maintainers" field with your npm user info.`}</p>
    <h3>{`funding`}</h3>
    <p>{`You can specify an object containing a URL that provides up-to-date
information about ways to help fund development of your package, or a
string URL, or an array of these:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "funding": {
    "type" : "individual",
    "url" : "http://example.com/donate"
  },

  "funding": {
    "type" : "patreon",
    "url" : "https://www.patreon.com/my-account"
  },

  "funding": "http://example.com/donate",

  "funding": [
    {
      "type" : "individual",
      "url" : "http://example.com/donate"
    },
    "http://example.com/donateAlso",
    {
      "type" : "patreon",
      "url" : "https://www.patreon.com/my-account"
    }
  ]
}
`}</code></pre>
    <p>{`Users can use the `}<inlineCode parentName="p">{`npm fund`}</inlineCode>{` subcommand to list the `}<inlineCode parentName="p">{`funding`}</inlineCode>{` URLs of all
dependencies of their project, direct and indirect. A shortcut to visit
each funding url is also available when providing the project name such as:
`}<inlineCode parentName="p">{`npm fund <projectname>`}</inlineCode>{` (when there are multiple URLs, the first one will
be visited)`}</p>
    <h3>{`files`}</h3>
    <p>{`The optional `}<inlineCode parentName="p">{`files`}</inlineCode>{` field is an array of file patterns that describes the
entries to be included when your package is installed as a dependency. File
patterns follow a similar syntax to `}<inlineCode parentName="p">{`.gitignore`}</inlineCode>{`, but reversed: including a
file, directory, or glob pattern (`}<inlineCode parentName="p">{`*`}</inlineCode>{`, `}<inlineCode parentName="p">{`**/*`}</inlineCode>{`, and such) will make it so
that file is included in the tarball when it's packed. Omitting the field
will make it default to `}<inlineCode parentName="p">{`["*"]`}</inlineCode>{`, which means it will include all files.`}</p>
    <p>{`Some special files and directories are also included or excluded regardless
of whether they exist in the `}<inlineCode parentName="p">{`files`}</inlineCode>{` array (see below).`}</p>
    <p>{`You can also provide a `}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` file in the root of your package or in
subdirectories, which will keep files from being included. At the root of
your package it will not override the "files" field, but in subdirectories
it will. The `}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` file works just like a `}<inlineCode parentName="p">{`.gitignore`}</inlineCode>{`. If there is
a `}<inlineCode parentName="p">{`.gitignore`}</inlineCode>{` file, and `}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` is missing, `}<inlineCode parentName="p">{`.gitignore`}</inlineCode>{`'s contents
will be used instead.`}</p>
    <p>{`Files included with the "package.json#files" field `}<em parentName="p">{`cannot`}</em>{` be excluded
through `}<inlineCode parentName="p">{`.npmignore`}</inlineCode>{` or `}<inlineCode parentName="p">{`.gitignore`}</inlineCode>{`.`}</p>
    <p>{`Certain files are always included, regardless of settings:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`package.json`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`README`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`LICENSE`}</inlineCode>{` / `}<inlineCode parentName="li">{`LICENCE`}</inlineCode></li>
      <li parentName="ul">{`The file in the "main" field`}</li>
    </ul>
    <p><inlineCode parentName="p">{`README`}</inlineCode>{` & `}<inlineCode parentName="p">{`LICENSE`}</inlineCode>{` can have any case and extension.`}</p>
    <p>{`Conversely, some files are always ignored:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`.git`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`CVS`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.svn`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.hg`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.lock-wscript`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.wafpickle-N`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.*.swp`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.DS_Store`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`._*`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`npm-debug.log`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`.npmrc`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`node_modules`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`config.gypi`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`*.orig`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`package-lock.json`}</inlineCode>{` (use
`}<a parentName="li" {...{
          "href": "/cli/v8/configuring-npm/npm-shrinkwrap-json"
        }}><inlineCode parentName="a">{`npm-shrinkwrap.json`}</inlineCode></a>{` if you wish
it to be published)`}</li>
    </ul>
    <h3>{`main`}</h3>
    <p>{`The main field is a module ID that is the primary entry point to your
program.  That is, if your package is named `}<inlineCode parentName="p">{`foo`}</inlineCode>{`, and a user installs it,
and then does `}<inlineCode parentName="p">{`require("foo")`}</inlineCode>{`, then your main module's exports object will
be returned.`}</p>
    <p>{`This should be a module relative to the root of your package folder.`}</p>
    <p>{`For most modules, it makes the most sense to have a main script and often
not much else.`}</p>
    <p>{`If `}<inlineCode parentName="p">{`main`}</inlineCode>{` is not set it defaults to `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` in the package's root folder.`}</p>
    <h3>{`browser`}</h3>
    <p>{`If your module is meant to be used client-side the browser field should be
used instead of the main field. This is helpful to hint users that it might
rely on primitives that aren't available in Node.js modules. (e.g.
`}<inlineCode parentName="p">{`window`}</inlineCode>{`)`}</p>
    <h3>{`bin`}</h3>
    <p>{`A lot of packages have one or more executable files that they'd like to
install into the PATH. npm makes this pretty easy (in fact, it uses this
feature to install the "npm" executable.)`}</p>
    <p>{`To use this, supply a `}<inlineCode parentName="p">{`bin`}</inlineCode>{` field in your package.json which is a map of
command name to local file name. When this package is installed
globally, that file will be linked where global bins go so it is
available to run by name.  When this package is installed as a
dependency in another package, the file will be linked where it will be
available to that package either directly by `}<inlineCode parentName="p">{`npm exec`}</inlineCode>{` or by name in other
scripts when invoking them via `}<inlineCode parentName="p">{`npm run-script`}</inlineCode>{`.`}</p>
    <p>{`For example, myapp could have this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "bin": {
    "myapp": "./cli.js"
  }
}
`}</code></pre>
    <p>{`So, when you install myapp, it'll create a symlink from the `}<inlineCode parentName="p">{`cli.js`}</inlineCode>{` script
to `}<inlineCode parentName="p">{`/usr/local/bin/myapp`}</inlineCode>{`.`}</p>
    <p>{`If you have a single executable, and its name should be the name of the
package, then you can just supply it as a string.  For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "my-program",
  "version": "1.2.5",
  "bin": "./path/to/program"
}
`}</code></pre>
    <p>{`would be the same as this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "my-program",
  "version": "1.2.5",
  "bin": {
    "my-program": "./path/to/program"
  }
}
`}</code></pre>
    <p>{`Please make sure that your file(s) referenced in `}<inlineCode parentName="p">{`bin`}</inlineCode>{` starts with
`}<inlineCode parentName="p">{`#!/usr/bin/env node`}</inlineCode>{`, otherwise the scripts are started without the node
executable!`}</p>
    <p>{`Note that you can also set the executable files using `}<a parentName="p" {...{
        "href": "#directoriesbin"
      }}>{`directories.bin`}</a>{`.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "/cli/v8/configuring-npm/folders#executables"
      }}>{`folders`}</a>{` for more info on
executables.`}</p>
    <h3>{`man`}</h3>
    <p>{`Specify either a single file or an array of filenames to put in place for
the `}<inlineCode parentName="p">{`man`}</inlineCode>{` program to find.`}</p>
    <p>{`If only a single file is provided, then it's installed such that it is the
result from `}<inlineCode parentName="p">{`man <pkgname>`}</inlineCode>{`, regardless of its actual filename.  For
example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "foo",
  "version": "1.2.3",
  "description": "A packaged foo fooer for fooing foos",
  "main": "foo.js",
  "man": "./man/doc.1"
}
`}</code></pre>
    <p>{`would link the `}<inlineCode parentName="p">{`./man/doc.1`}</inlineCode>{` file in such that it is the target for `}<inlineCode parentName="p">{`man
foo`}</inlineCode></p>
    <p>{`If the filename doesn't start with the package name, then it's prefixed.
So, this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "foo",
  "version": "1.2.3",
  "description": "A packaged foo fooer for fooing foos",
  "main": "foo.js",
  "man": [
    "./man/foo.1",
    "./man/bar.1"
  ]
}
`}</code></pre>
    <p>{`will create files to do `}<inlineCode parentName="p">{`man foo`}</inlineCode>{` and `}<inlineCode parentName="p">{`man foo-bar`}</inlineCode>{`.`}</p>
    <p>{`Man files must end with a number, and optionally a `}<inlineCode parentName="p">{`.gz`}</inlineCode>{` suffix if they are
compressed.  The number dictates which man section the file is installed
into.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "foo",
  "version": "1.2.3",
  "description": "A packaged foo fooer for fooing foos",
  "main": "foo.js",
  "man": [
    "./man/foo.1",
    "./man/foo.2"
  ]
}
`}</code></pre>
    <p>{`will create entries for `}<inlineCode parentName="p">{`man foo`}</inlineCode>{` and `}<inlineCode parentName="p">{`man 2 foo`}</inlineCode></p>
    <h3>{`directories`}</h3>
    <p>{`The CommonJS `}<a parentName="p" {...{
        "href": "http://wiki.commonjs.org/wiki/Packages/1.0"
      }}>{`Packages`}</a>{` spec
details a few ways that you can indicate the structure of your package
using a `}<inlineCode parentName="p">{`directories`}</inlineCode>{` object. If you look at `}<a parentName="p" {...{
        "href": "https://registry.npmjs.org/npm/latest"
      }}>{`npm's
package.json`}</a>{`, you'll see that it
has directories for doc, lib, and man.`}</p>
    <p>{`In the future, this information may be used in other creative ways.`}</p>
    <h4>{`directories.bin`}</h4>
    <p>{`If you specify a `}<inlineCode parentName="p">{`bin`}</inlineCode>{` directory in `}<inlineCode parentName="p">{`directories.bin`}</inlineCode>{`, all the files in
that folder will be added.`}</p>
    <p>{`Because of the way the `}<inlineCode parentName="p">{`bin`}</inlineCode>{` directive works, specifying both a `}<inlineCode parentName="p">{`bin`}</inlineCode>{` path
and setting `}<inlineCode parentName="p">{`directories.bin`}</inlineCode>{` is an error. If you want to specify
individual files, use `}<inlineCode parentName="p">{`bin`}</inlineCode>{`, and for all the files in an existing `}<inlineCode parentName="p">{`bin`}</inlineCode>{`
directory, use `}<inlineCode parentName="p">{`directories.bin`}</inlineCode>{`.`}</p>
    <h4>{`directories.man`}</h4>
    <p>{`A folder that is full of man pages.  Sugar to generate a "man" array by
walking the folder.`}</p>
    <h3>{`repository`}</h3>
    <p>{`Specify the place where your code lives. This is helpful for people who
want to contribute.  If the git repo is on GitHub, then the `}<inlineCode parentName="p">{`npm docs`}</inlineCode>{`
command will be able to find you.`}</p>
    <p>{`Do it like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "repository": {
    "type": "git",
    "url": "https://github.com/npm/cli.git"
  }
}
`}</code></pre>
    <p>{`The URL should be a publicly available (perhaps read-only) url that can be
handed directly to a VCS program without any modification.  It should not
be a url to an html project page that you put in your browser.  It's for
computers.`}</p>
    <p>{`For GitHub, GitHub gist, Bitbucket, or GitLab repositories you can use the
same shortcut syntax you use for `}<inlineCode parentName="p">{`npm install`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "repository": "npm/npm",

  "repository": "github:user/repo",

  "repository": "gist:11081aaa281",

  "repository": "bitbucket:user/repo",

  "repository": "gitlab:user/repo"
}
`}</code></pre>
    <p>{`If the `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` for your package is not in the root directory (for
example if it is part of a monorepo), you can specify the directory in
which it lives:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "repository": {
    "type": "git",
    "url": "https://github.com/facebook/react.git",
    "directory": "packages/react-dom"
  }
}
`}</code></pre>
    <h3>{`scripts`}</h3>
    <p>{`The "scripts" property is a dictionary containing script commands that are
run at various times in the lifecycle of your package.  The key is the
lifecycle event, and the value is the command to run at that point.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/scripts"
      }}><inlineCode parentName="a">{`scripts`}</inlineCode></a>{` to find out more about writing package
scripts.`}</p>
    <h3>{`config`}</h3>
    <p>{`A "config" object can be used to set configuration parameters used in
package scripts that persist across upgrades.  For instance, if a package
had the following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "foo",
  "config": {
    "port": "8080"
  }
}
`}</code></pre>
    <p>{`It could also have a "start" command that referenced the
`}<inlineCode parentName="p">{`npm_package_config_port`}</inlineCode>{` environment variable.`}</p>
    <h3>{`dependencies`}</h3>
    <p>{`Dependencies are specified in a simple object that maps a package name to a
version range. The version range is a string which has one or more
space-separated descriptors.  Dependencies can also be identified with a
tarball or git URL.`}</p>
    <p><strong parentName="p">{`Please do not put test harnesses or transpilers or other "development"
time tools in your `}<inlineCode parentName="strong">{`dependencies`}</inlineCode>{` object.`}</strong>{`  See `}<inlineCode parentName="p">{`devDependencies`}</inlineCode>{`, below.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://github.com/npm/node-semver#versions"
      }}>{`semver`}</a>{` for more details about specifying version ranges.`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`version`}</inlineCode>{` Must match `}<inlineCode parentName="li">{`version`}</inlineCode>{` exactly`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`>version`}</inlineCode>{` Must be greater than `}<inlineCode parentName="li">{`version`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`>=version`}</inlineCode>{` etc`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`<version`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`<=version`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`~version`}</inlineCode>{` "Approximately equivalent to version"  See
`}<a parentName="li" {...{
          "href": "https://github.com/npm/node-semver#versions"
        }}>{`semver`}</a></li>
      <li parentName="ul"><inlineCode parentName="li">{`^version`}</inlineCode>{` "Compatible with version"  See `}<a parentName="li" {...{
          "href": "https://github.com/npm/node-semver#versions"
        }}>{`semver`}</a></li>
      <li parentName="ul"><inlineCode parentName="li">{`1.2.x`}</inlineCode>{` 1.2.0, 1.2.1, etc., but not 1.3.0`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`http://...`}</inlineCode>{` See 'URLs as Dependencies' below`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`*`}</inlineCode>{` Matches any version`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`""`}</inlineCode>{` (just an empty string) Same as `}<inlineCode parentName="li">{`*`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`version1 - version2`}</inlineCode>{` Same as `}<inlineCode parentName="li">{`>=version1 <=version2`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`range1 || range2`}</inlineCode>{` Passes if either range1 or range2 are satisfied.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`git...`}</inlineCode>{` See 'Git URLs as Dependencies' below`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`user/repo`}</inlineCode>{` See 'GitHub URLs' below`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`tag`}</inlineCode>{` A specific version tagged and published as `}<inlineCode parentName="li">{`tag`}</inlineCode>{`  See `}<a parentName="li" {...{
          "href": "/cli/v8/commands/npm-dist-tag"
        }}><inlineCode parentName="a">{`npm
dist-tag`}</inlineCode></a></li>
      <li parentName="ul"><inlineCode parentName="li">{`path/path/path`}</inlineCode>{` See `}<a parentName="li" {...{
          "href": "#local-paths"
        }}>{`Local Paths`}</a>{` below`}</li>
    </ul>
    <p>{`For example, these are all valid:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "dependencies": {
    "foo": "1.0.0 - 2.9999.9999",
    "bar": ">=1.0.2 <2.1.2",
    "baz": ">1.0.2 <=2.3.4",
    "boo": "2.0.1",
    "qux": "<1.0.0 || >=2.3.1 <2.4.5 || >=2.5.2 <3.0.0",
    "asd": "http://asdf.com/asdf.tar.gz",
    "til": "~1.2",
    "elf": "~1.2.3",
    "two": "2.x",
    "thr": "3.3.x",
    "lat": "latest",
    "dyl": "file:../dyl"
  }
}
`}</code></pre>
    <h4>{`URLs as Dependencies`}</h4>
    <p>{`You may specify a tarball URL in place of a version range.`}</p>
    <p>{`This tarball will be downloaded and installed locally to your package at
install time.`}</p>
    <h4>{`Git URLs as Dependencies`}</h4>
    <p>{`Git urls are of the form:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`<protocol>://[<user>[:<password>]@]<hostname>[:<port>][:][/]<path>[#<commit-ish> | #semver:<semver>]
`}</code></pre>
    <p><inlineCode parentName="p">{`<protocol>`}</inlineCode>{` is one of `}<inlineCode parentName="p">{`git`}</inlineCode>{`, `}<inlineCode parentName="p">{`git+ssh`}</inlineCode>{`, `}<inlineCode parentName="p">{`git+http`}</inlineCode>{`, `}<inlineCode parentName="p">{`git+https`}</inlineCode>{`, or
`}<inlineCode parentName="p">{`git+file`}</inlineCode>{`.`}</p>
    <p>{`If `}<inlineCode parentName="p">{`#<commit-ish>`}</inlineCode>{` is provided, it will be used to clone exactly that
commit. If the commit-ish has the format `}<inlineCode parentName="p">{`#semver:<semver>`}</inlineCode>{`, `}<inlineCode parentName="p">{`<semver>`}</inlineCode>{` can
be any valid semver range or exact version, and npm will look for any tags
or refs matching that range in the remote repository, much as it would for
a registry dependency. If neither `}<inlineCode parentName="p">{`#<commit-ish>`}</inlineCode>{` or `}<inlineCode parentName="p">{`#semver:<semver>`}</inlineCode>{` is
specified, then the default branch is used.`}</p>
    <p>{`Examples:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git+ssh://git@github.com:npm/cli.git#v1.0.27
git+ssh://git@github.com:npm/cli#semver:^5.0
git+https://isaacs@github.com/npm/cli.git
git://github.com/npm/cli.git#v1.0.27
`}</code></pre>
    <p>{`When installing from a `}<inlineCode parentName="p">{`git`}</inlineCode>{` repository, the presence of certain fields in the
`}<inlineCode parentName="p">{`package.json`}</inlineCode>{` will cause npm to believe it needs to perform a build. To do so
your repository will be cloned into a temporary directory, all of its deps
installed, relevant scripts run, and the resulting directory packed and
installed.`}</p>
    <p>{`This flow will occur if your git dependency uses `}<inlineCode parentName="p">{`workspaces`}</inlineCode>{`, or if any of the
following scripts are present:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`build`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`prepare`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`prepack`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`preinstall`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`install`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`postinstall`}</inlineCode></li>
    </ul>
    <p>{`If your git repository includes pre-built artifacts, you will likely want to
make sure that none of the above scripts are defined, or your dependency
will be rebuilt for every installation.`}</p>
    <h4>{`GitHub URLs`}</h4>
    <p>{`As of version 1.1.65, you can refer to GitHub urls as just "foo":
"user/foo-project".  Just as with git URLs, a `}<inlineCode parentName="p">{`commit-ish`}</inlineCode>{` suffix can be
included.  For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "foo",
  "version": "0.0.0",
  "dependencies": {
    "express": "expressjs/express",
    "mocha": "mochajs/mocha#4727d357ea",
    "module": "user/repo#feature\\/branch"
  }
}
`}</code></pre>
    <h4>{`Local Paths`}</h4>
    <p>{`As of version 2.0.0 you can provide a path to a local directory that
contains a package. Local paths can be saved using `}<inlineCode parentName="p">{`npm install -S`}</inlineCode>{` or `}<inlineCode parentName="p">{`npm
install --save`}</inlineCode>{`, using any of these forms:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`../foo/bar
~/foo/bar
./foo/bar
/foo/bar
`}</code></pre>
    <p>{`in which case they will be normalized to a relative path and added to your
`}<inlineCode parentName="p">{`package.json`}</inlineCode>{`. For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "baz",
  "dependencies": {
    "bar": "file:../foo/bar"
  }
}
`}</code></pre>
    <p>{`This feature is helpful for local offline development and creating tests
that require npm installing where you don't want to hit an external server,
but should not be used when publishing packages to the public registry.`}</p>
    <p><em parentName="p">{`note`}</em>{`: Packages linked by local path will not have their own
dependencies installed when `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` is ran in this case.  You must
run `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` from inside the local path itself.`}</p>
    <h3>{`devDependencies`}</h3>
    <p>{`If someone is planning on downloading and using your module in their
program, then they probably don't want or need to download and build the
external test or documentation framework that you use.`}</p>
    <p>{`In this case, it's best to map these additional items in a
`}<inlineCode parentName="p">{`devDependencies`}</inlineCode>{` object.`}</p>
    <p>{`These things will be installed when doing `}<inlineCode parentName="p">{`npm link`}</inlineCode>{` or `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` from
the root of a package, and can be managed like any other npm configuration
param.  See `}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/config"
      }}><inlineCode parentName="a">{`config`}</inlineCode></a>{` for more on the topic.`}</p>
    <p>{`For build steps that are not platform-specific, such as compiling
CoffeeScript or other languages to JavaScript, use the `}<inlineCode parentName="p">{`prepare`}</inlineCode>{` script to
do this, and make the required package a devDependency.`}</p>
    <p>{`For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "ethopia-waza",
  "description": "a delightfully fruity coffee varietal",
  "version": "1.2.3",
  "devDependencies": {
    "coffee-script": "~1.6.3"
  },
  "scripts": {
    "prepare": "coffee -o lib/ -c src/waza.coffee"
  },
  "main": "lib/waza.js"
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`prepare`}</inlineCode>{` script will be run before publishing, so that users can
consume the functionality without requiring them to compile it themselves.
In dev mode (ie, locally running `}<inlineCode parentName="p">{`npm install`}</inlineCode>{`), it'll run this script as
well, so that you can test it easily.`}</p>
    <h3>{`peerDependencies`}</h3>
    <p>{`In some cases, you want to express the compatibility of your package with a
host tool or library, while not necessarily doing a `}<inlineCode parentName="p">{`require`}</inlineCode>{` of this host.
This is usually referred to as a `}<em parentName="p">{`plugin`}</em>{`. Notably, your module may be
exposing a specific interface, expected and specified by the host
documentation.`}</p>
    <p>{`For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "tea-latte",
  "version": "1.3.5",
  "peerDependencies": {
    "tea": "2.x"
  }
}
`}</code></pre>
    <p>{`This ensures your package `}<inlineCode parentName="p">{`tea-latte`}</inlineCode>{` can be installed `}<em parentName="p">{`along`}</em>{` with the
second major version of the host package `}<inlineCode parentName="p">{`tea`}</inlineCode>{` only. `}<inlineCode parentName="p">{`npm install
tea-latte`}</inlineCode>{` could possibly yield the following dependency graph:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`├── tea-latte@1.3.5
└── tea@2.2.0
`}</code></pre>
    <p>{`In npm versions 3 through 6, `}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` were not automatically
installed, and would raise a warning if an invalid version of the peer
dependency was found in the tree.  As of npm v7, peerDependencies `}<em parentName="p">{`are`}</em>{`
installed by default.`}</p>
    <p>{`Trying to install another plugin with a conflicting requirement may cause
an error if the tree cannot be resolved correctly. For this reason, make
sure your plugin requirement is as broad as possible, and not to lock it
down to specific patch versions.`}</p>
    <p>{`Assuming the host complies with `}<a parentName="p" {...{
        "href": "https://semver.org/"
      }}>{`semver`}</a>{`, only changes
in the host package's major version will break your plugin. Thus, if you've
worked with every 1.x version of the host package, use `}<inlineCode parentName="p">{`"^1.0"`}</inlineCode>{` or `}<inlineCode parentName="p">{`"1.x"`}</inlineCode>{`
to express this. If you depend on features introduced in 1.5.2, use
`}<inlineCode parentName="p">{`"^1.5.2"`}</inlineCode>{`.`}</p>
    <h3>{`peerDependenciesMeta`}</h3>
    <p>{`When a user installs your package, npm will emit warnings if packages
specified in `}<inlineCode parentName="p">{`peerDependencies`}</inlineCode>{` are not already installed. The
`}<inlineCode parentName="p">{`peerDependenciesMeta`}</inlineCode>{` field serves to provide npm more information on how
your peer dependencies are to be used. Specifically, it allows peer
dependencies to be marked as optional.`}</p>
    <p>{`For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "tea-latte",
  "version": "1.3.5",
  "peerDependencies": {
    "tea": "2.x",
    "soy-milk": "1.2"
  },
  "peerDependenciesMeta": {
    "soy-milk": {
      "optional": true
    }
  }
}
`}</code></pre>
    <p>{`Marking a peer dependency as optional ensures npm will not emit a warning
if the `}<inlineCode parentName="p">{`soy-milk`}</inlineCode>{` package is not installed on the host. This allows you to
integrate and interact with a variety of host packages without requiring
all of them to be installed.`}</p>
    <h3>{`bundleDependencies`}</h3>
    <p>{`This defines an array of package names that will be bundled when publishing
the package.`}</p>
    <p>{`In cases where you need to preserve npm packages locally or have them
available through a single file download, you can bundle the packages in a
tarball file by specifying the package names in the `}<inlineCode parentName="p">{`bundleDependencies`}</inlineCode>{`
array and executing `}<inlineCode parentName="p">{`npm pack`}</inlineCode>{`.`}</p>
    <p>{`For example:`}</p>
    <p>{`If we define a package.json like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "awesome-web-framework",
  "version": "1.0.0",
  "bundleDependencies": [
    "renderized",
    "super-streams"
  ]
}
`}</code></pre>
    <p>{`we can obtain `}<inlineCode parentName="p">{`awesome-web-framework-1.0.0.tgz`}</inlineCode>{` file by running `}<inlineCode parentName="p">{`npm pack`}</inlineCode>{`.
This file contains the dependencies `}<inlineCode parentName="p">{`renderized`}</inlineCode>{` and `}<inlineCode parentName="p">{`super-streams`}</inlineCode>{` which
can be installed in a new project by executing `}<inlineCode parentName="p">{`npm install
awesome-web-framework-1.0.0.tgz`}</inlineCode>{`.  Note that the package names do not
include any versions, as that information is specified in `}<inlineCode parentName="p">{`dependencies`}</inlineCode>{`.`}</p>
    <p>{`If this is spelled `}<inlineCode parentName="p">{`"bundledDependencies"`}</inlineCode>{`, then that is also honored.`}</p>
    <p>{`Alternatively, `}<inlineCode parentName="p">{`"bundleDependencies"`}</inlineCode>{` can be defined as a boolean value. A
value of `}<inlineCode parentName="p">{`true`}</inlineCode>{` will bundle all dependencies, a value of `}<inlineCode parentName="p">{`false`}</inlineCode>{` will bundle
none.`}</p>
    <h3>{`optionalDependencies`}</h3>
    <p>{`If a dependency can be used, but you would like npm to proceed if it cannot
be found or fails to install, then you may put it in the
`}<inlineCode parentName="p">{`optionalDependencies`}</inlineCode>{` object.  This is a map of package name to version or
url, just like the `}<inlineCode parentName="p">{`dependencies`}</inlineCode>{` object.  The difference is that build
failures do not cause installation to fail.  Running `}<inlineCode parentName="p">{`npm install
--no-optional`}</inlineCode>{` will prevent these dependencies from being installed.`}</p>
    <p>{`It is still your program's responsibility to handle the lack of the
dependency.  For example, something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`try {
  var foo = require('foo')
  var fooVersion = require('foo/package.json').version
} catch (er) {
  foo = null
}
if ( notGoodFooVersion(fooVersion) ) {
  foo = null
}

// .. then later in your program ..

if (foo) {
  foo.doFooThings()
}
`}</code></pre>
    <p>{`Entries in `}<inlineCode parentName="p">{`optionalDependencies`}</inlineCode>{` will override entries of the same name in
`}<inlineCode parentName="p">{`dependencies`}</inlineCode>{`, so it's usually best to only put in one place.`}</p>
    <h3>{`overrides`}</h3>
    <p>{`If you need to make specific changes to dependencies of your dependencies, for
example replacing the version of a dependency with a known security issue,
replacing an existing dependency with a fork, or making sure that the same
version of a package is used everywhere, then you may add an override.`}</p>
    <p>{`Overrides provide a way to replace a package in your dependency tree with
another version, or another package entirely. These changes can be scoped as
specific or as vague as desired.`}</p>
    <p>{`To make sure the package `}<inlineCode parentName="p">{`foo`}</inlineCode>{` is always installed as version `}<inlineCode parentName="p">{`1.0.0`}</inlineCode>{` no matter
what version your dependencies rely on:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "overrides": {
    "foo": "1.0.0"
  }
}
`}</code></pre>
    <p>{`The above is a short hand notation, the full object form can be used to allow
overriding a package itself as well as a child of the package. This will cause
`}<inlineCode parentName="p">{`foo`}</inlineCode>{` to always be `}<inlineCode parentName="p">{`1.0.0`}</inlineCode>{` while also making `}<inlineCode parentName="p">{`bar`}</inlineCode>{` at any depth beyond `}<inlineCode parentName="p">{`foo`}</inlineCode>{`
also `}<inlineCode parentName="p">{`1.0.0`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "overrides": {
    "foo": {
      ".": "1.0.0",
      "bar": "1.0.0"
    }
  }
}
`}</code></pre>
    <p>{`To only override `}<inlineCode parentName="p">{`foo`}</inlineCode>{` to be `}<inlineCode parentName="p">{`1.0.0`}</inlineCode>{` when it's a child (or grandchild, or great
grandchild, etc) of the package `}<inlineCode parentName="p">{`bar`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "overrides": {
    "bar": {
      "foo": "1.0.0"
    }
  }
}
`}</code></pre>
    <p>{`Keys can be nested to any arbitrary length. To override `}<inlineCode parentName="p">{`foo`}</inlineCode>{` only when it's a
child of `}<inlineCode parentName="p">{`bar`}</inlineCode>{` and only when `}<inlineCode parentName="p">{`bar`}</inlineCode>{` is a child of `}<inlineCode parentName="p">{`baz`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "overrides": {
    "baz": {
      "bar": {
        "foo": "1.0.0"
      }
    }
  }
}
`}</code></pre>
    <p>{`The key of an override can also include a version, or range of versions.
To override `}<inlineCode parentName="p">{`foo`}</inlineCode>{` to `}<inlineCode parentName="p">{`1.0.0`}</inlineCode>{`, but only when it's a child of `}<inlineCode parentName="p">{`bar@2.0.0`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "overrides": {
    "bar@2.0.0": {
      "foo": "1.0.0"
    }
  }
}
`}</code></pre>
    <p>{`You may not set an override for a package that you directly depend on unless
both the dependency and the override itself share the exact same spec. To make
this limitation easier to deal with, overrides may also be defined as a
reference to a spec for a direct dependency by prefixing the name of the
package you wish the version to match with a `}<inlineCode parentName="p">{`$`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "dependencies": {
    "foo": "^1.0.0"
  },
  "overrides": {
    // BAD, will throw an EOVERRIDE error
    // "foo": "^2.0.0"
    // GOOD, specs match so override is allowed
    // "foo": "^1.0.0"
    // BEST, the override is defined as a reference to the dependency
    "foo": "$foo",
    // the referenced package does not need to match the overridden one
    "bar": "$foo"
  }
}
`}</code></pre>
    <h3>{`engines`}</h3>
    <p>{`You can specify the version of node that your stuff works on:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "engines": {
    "node": ">=0.10.3 <15"
  }
}
`}</code></pre>
    <p>{`And, like with dependencies, if you don't specify the version (or if you
specify "`}{`*`}{`" as the version), then any version of node will do.`}</p>
    <p>{`You can also use the "engines" field to specify which versions of npm are
capable of properly installing your program.  For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "engines": {
    "npm": "~1.0.20"
  }
}
`}</code></pre>
    <p>{`Unless the user has set the `}<inlineCode parentName="p">{`engine-strict`}</inlineCode>{` config flag, this field is
advisory only and will only produce warnings when your package is installed
as a dependency.`}</p>
    <h3>{`os`}</h3>
    <p>{`You can specify which operating systems your
module will run on:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "os": [
    "darwin",
    "linux"
  ]
}
`}</code></pre>
    <p>{`You can also block instead of allowing operating systems, just prepend the
blocked os with a '!':`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "os": [
    "!win32"
  ]
}
`}</code></pre>
    <p>{`The host operating system is determined by `}<inlineCode parentName="p">{`process.platform`}</inlineCode></p>
    <p>{`It is allowed to both block and allow an item, although there isn't any
good reason to do this.`}</p>
    <h3>{`cpu`}</h3>
    <p>{`If your code only runs on certain cpu architectures,
you can specify which ones.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "cpu": [
    "x64",
    "ia32"
  ]
}
`}</code></pre>
    <p>{`Like the `}<inlineCode parentName="p">{`os`}</inlineCode>{` option, you can also block architectures:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "cpu": [
    "!arm",
    "!mips"
  ]
}
`}</code></pre>
    <p>{`The host architecture is determined by `}<inlineCode parentName="p">{`process.arch`}</inlineCode></p>
    <h3>{`private`}</h3>
    <p>{`If you set `}<inlineCode parentName="p">{`"private": true`}</inlineCode>{` in your package.json, then npm will refuse to
publish it.`}</p>
    <p>{`This is a way to prevent accidental publication of private repositories.
If you would like to ensure that a given package is only ever published to
a specific registry (for example, an internal registry), then use the
`}<inlineCode parentName="p">{`publishConfig`}</inlineCode>{` dictionary described below to override the `}<inlineCode parentName="p">{`registry`}</inlineCode>{`
config param at publish-time.`}</p>
    <h3>{`publishConfig`}</h3>
    <p>{`This is a set of config values that will be used at publish-time. It's
especially handy if you want to set the tag, registry or access, so that
you can ensure that a given package is not tagged with "latest", published
to the global public registry or that a scoped module is private by
default.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/config"
      }}><inlineCode parentName="a">{`config`}</inlineCode></a>{` to see the list of config options that
can be overridden.`}</p>
    <h3>{`workspaces`}</h3>
    <p>{`The optional `}<inlineCode parentName="p">{`workspaces`}</inlineCode>{` field is an array of file patterns that describes
locations within the local file system that the install client should look
up to find each `}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/workspaces"
      }}>{`workspace`}</a>{` that needs to be
symlinked to the top level `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder.`}</p>
    <p>{`It can describe either the direct paths of the folders to be used as
workspaces or it can define globs that will resolve to these same folders.`}</p>
    <p>{`In the following example, all folders located inside the folder
`}<inlineCode parentName="p">{`./packages`}</inlineCode>{` will be treated as workspaces as long as they have valid
`}<inlineCode parentName="p">{`package.json`}</inlineCode>{` files inside them:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "workspace-example",
  "workspaces": [
    "./packages/*"
  ]
}
`}</code></pre>
    <p>{`See `}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/workspaces"
      }}><inlineCode parentName="a">{`workspaces`}</inlineCode></a>{` for more examples.`}</p>
    <h3>{`DEFAULT VALUES`}</h3>
    <p>{`npm will default some values based on package contents.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`"scripts": {"start": "node server.js"}`}</inlineCode></p>
        <p parentName="li">{`If there is a `}<inlineCode parentName="p">{`server.js`}</inlineCode>{` file in the root of your package, then npm will
default the `}<inlineCode parentName="p">{`start`}</inlineCode>{` command to `}<inlineCode parentName="p">{`node server.js`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`"scripts":{"install": "node-gyp rebuild"}`}</inlineCode></p>
        <p parentName="li">{`If there is a `}<inlineCode parentName="p">{`binding.gyp`}</inlineCode>{` file in the root of your package and you have
not defined an `}<inlineCode parentName="p">{`install`}</inlineCode>{` or `}<inlineCode parentName="p">{`preinstall`}</inlineCode>{` script, npm will default the
`}<inlineCode parentName="p">{`install`}</inlineCode>{` command to compile using node-gyp.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`"contributors": [...]`}</inlineCode></p>
        <p parentName="li">{`If there is an `}<inlineCode parentName="p">{`AUTHORS`}</inlineCode>{` file in the root of your package, npm will treat
each line as a `}<inlineCode parentName="p">{`Name <email> (url)`}</inlineCode>{` format, where email and url are
optional.  Lines which start with a `}<inlineCode parentName="p">{`#`}</inlineCode>{` or are blank, will be ignored.`}</p>
      </li>
    </ul>
    <h3>{`SEE ALSO`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/npm/node-semver#versions"
        }}>{`semver`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/using-npm/workspaces"
        }}>{`workspaces`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-init"
        }}>{`npm init`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-version"
        }}>{`npm version`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-config"
        }}>{`npm config`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-help"
        }}>{`npm help`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-install"
        }}>{`npm install`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-publish"
        }}>{`npm publish`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-uninstall"
        }}>{`npm uninstall`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      